"use client"

import { ChevronDown, IndianRupee, GraduationCap, Calendar, Zap } from "lucide-react"
import { useState, useEffect } from "react"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useLocation, useNavigate } from "react-router-dom"
import ScholarshipContent from "./ScholarshipContent"
import ManageFees from "./ManageFees"
import { API_ENDPOINTS } from "../../../constants/config"
import MakeLiveFees from "./MakeLiveFees"
import FeeStructureTable from "./FeeStructureTable"
import StudentFeesContent from "./StudentFeesContent"

function FeeManagementContent() {
  const location = useLocation()
  const navigate = useNavigate()
  const [isManageFeesOpen, setIsManageFeesOpen] = useState(false)
  const [isFeeStructureOpen, setIsFeeStructureOpen] = useState(false)
  const [selectedClass, setSelectedClass] = useState("")
  const [academicYear, setAcademicYear] = useState("")
  const [fees, setFees] = useState([])
  const [classes, setClasses] = useState([])
  const [classData, setClassData] = useState([])
  const [academicYears, setAcademicYears] = useState([])
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  // Get the active tab from URL hash or default to fee-structure
  const getActiveTab = () => {
    const hash = location.hash.replace("#", "")
    const validTabs = ["student-fees", "fee-structure", "scholarship", "make-live"]
    return validTabs.includes(hash) ? hash : "fee-structure"
  }

  const [activeTab, setActiveTab] = useState(getActiveTab())

  // Update active tab when URL hash changes
  useEffect(() => {
    setActiveTab(getActiveTab())
  }, [location.hash])

  // Handle tab change
  const handleTabChange = (value) => {
    setActiveTab(value)
    // Update URL hash without triggering a page reload
    navigate(`${location.pathname}#${value}`, { replace: true })
  }

  // Fetch classes and academic years
  useEffect(() => {
    const fetchClassesAndYears = async () => {
      try {
        // Fetch classes - update URL to match backend route
        const classesResponse = await fetch(API_ENDPOINTS.classes, {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        })

        if (!classesResponse.ok) throw new Error("Failed to fetch classes")
        const classesData = await classesResponse.json()
        setClassData(classesData.data.classes)
        setClasses(classesData.data.classes.map((cls) => cls.name))

        // Fetch academic years - update URL to match backend route
        const yearsResponse = await fetch(API_ENDPOINTS.academicYears, {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        })

        if (!yearsResponse.ok) throw new Error("Failed to fetch academic years")
        const yearsData = await yearsResponse.json()
        // Format academic years as strings
        const formattedYears = yearsData.data.academicYears.map((year) => `${year.startYear}-${year.endYear}`)
        setAcademicYears(formattedYears)
      } catch (err) {
        console.error("Error fetching classes and years:", err)
        setError("Failed to load classes and academic years. Please try again.")
      }
    }

    fetchClassesAndYears()
  }, [])

  // In FeeStructureTable component, update the fetchFees function
  useEffect(() => {
    const fetchFees = async () => {
      if (!selectedClass || !academicYear) {
        console.log("Missing required params:", { selectedClass, academicYear })
        return
      }

      setLoading(true)
      try {
        console.log("Fetching fees with params:", {
          selectedClass,
          academicYear,
          url: API_ENDPOINTS.feesByClass(selectedClass, academicYear),
        })

        const response = await fetch(API_ENDPOINTS.feesByClass(selectedClass, academicYear), {
          credentials: "include",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || "Failed to fetch fees")
        }

        const data = await response.json()
        console.log("API Response:", data)

        if (!data || !data.data || !Array.isArray(data.data.fees)) {
          console.error("Unexpected data structure:", data)
          throw new Error("Invalid data structure received from server")
        }

        const transformedFees = data.data.fees.map((fee) => ({
          id: fee._id,
          name: fee.fee_name,
          amount: fee.fee_amount.toString(),
          type: fee.fee_type,
          changed: false,
        }))

        console.log("Transformed fees:", transformedFees)
        setFees(transformedFees)
        setError("")
      } catch (err) {
        console.error("Error fetching fees:", err)
        setError("Failed to load fees")
      } finally {
        setLoading(false)
      }
    }

    fetchFees()
  }, [selectedClass, academicYear])

  const tabConfig = [
    { value: "student-fees", label: "Student Fees", icon: GraduationCap },
    { value: "fee-structure", label: "Fee Structure", icon: IndianRupee },
    { value: "scholarship", label: "Scholarship", icon: Calendar },
    { value: "make-live", label: "Make Live", icon: Zap },
  ]

  return (
    <div className="min-h-screen">
      <div className="mx-auto">

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-gradient-to-r from-red-50 to-red-100/50 border border-red-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              </div>
              <p className="text-red-700 text-sm md:text-base font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Tabs Section */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl p-2">
            <TabsList className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-transparent p-0 h-auto">
              {tabConfig.map((tab) => {
                const Icon = tab.icon
                return (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all duration-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#0263cc] data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-lg hover:bg-slate-50 text-slate-600"
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{tab.label}</span>
                    <span className="sm:hidden text-xs">{tab.label.split(" ")[0]}</span>
                  </TabsTrigger>
                )
              })}
            </TabsList>
          </div>

          <TabsContent value="fee-structure" className="space-y-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl overflow-hidden">
              {/* Bus Fees Section */}
              <div className="border-b border-slate-100">
                <div
                  className={`flex items-center justify-between cursor-pointer p-6 transition-all duration-200 ${
                    isManageFeesOpen
                      ? "bg-slate-50"
                      : "bg-gradient-to-r from-[#0263cc] to-blue-600 text-white hover:from-[#0263cc]/90 hover:to-blue-600/90"
                  }`}
                  onClick={() => setIsManageFeesOpen(!isManageFeesOpen)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isManageFeesOpen ? "bg-[#0263cc] text-white" : "bg-white/20"
                      }`}
                    >
                      <IndianRupee className="w-4 h-4" />
                    </div>
                    <h4 className="text-lg font-semibold">Bus Fees</h4>
                  </div>
                  <div className={`transition-transform duration-200 ${isManageFeesOpen ? "rotate-180" : ""}`}>
                    <ChevronDown className="h-5 w-5" />
                  </div>
                </div>
                {isManageFeesOpen && (
                  <div className="p-6 bg-slate-50/50">
                    <ManageFees classes={classes} />
                  </div>
                )}
              </div>

              {/* Class Fee Structure Section */}
              <div>
                <div
                  className={`flex items-center justify-between cursor-pointer p-6 transition-all duration-200 ${
                    isFeeStructureOpen
                      ? "bg-slate-50"
                      : "bg-gradient-to-r from-[#0263cc] to-blue-600 text-white hover:from-[#0263cc]/90 hover:to-blue-600/90"
                  }`}
                  onClick={() => setIsFeeStructureOpen(!isFeeStructureOpen)}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isFeeStructureOpen ? "bg-[#0263cc] text-white" : "bg-white/20"
                      }`}
                    >
                      <GraduationCap className="w-4 h-4" />
                    </div>
                    <h4 className="text-lg font-semibold">Class Fee Structure</h4>
                  </div>
                  <div className={`transition-transform duration-200 ${isFeeStructureOpen ? "rotate-180" : ""}`}>
                    <ChevronDown className="h-5 w-5" />
                  </div>
                </div>
                {isFeeStructureOpen && (
                  <div className="p-6 bg-slate-50/50">
                    <FeeStructureTable />
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="student-fees">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl">
              <StudentFeesContent classes={classes} />
            </div>
          </TabsContent>

          <TabsContent value="scholarship">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl">
              <ScholarshipContent />
            </div>
          </TabsContent>

          <TabsContent value="make-live">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl">
              <MakeLiveFees />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

export default FeeManagementContent
